import { useEffect, useRef } from "react";
import { useImmer } from "use-immer";
import {
  useExport,
  EXPORT_STATUS_INIT,
  EXPORT_STATUS_FILE_EXISTS,
  EXPORT_STATUS_IN_PROGRESS,
  EXPORT_STATUS_DONE,
  EXPORT_STATUS_FAILED,
} from "../context/ExportContext";

const Component = ({
  caption,
  containerClassName,
  disabled,
  filterKey,
  exportDateTimeFormat,
  onExportDateTimeFormatChange,
  onFirstClick,
  onForceClick,
  onLoadFiles,
}) => {
  const {
    status,
    existingFileName,
    presignedUrl,
    progress,
    errorMessage,
    isExportActive,
    startJob,
    setFileExists,
    reset,
    cancelJob,
    getEta,
  } = useExport();

  // When filters change after a completed export, reset so the user can start a new one.
  // Skip the initial mount — we only want to reset on *subsequent* filterKey changes.
  const isMounted = useRef(false);
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    if (status === EXPORT_STATUS_DONE) {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // File list panel — local UI state only, no need to survive navigation
  const [filesOpen, updateFilesOpen] = useImmer(false);
  const [files, updateFiles] = useImmer([]);
  const [filesLoading, updateFilesLoading] = useImmer(false);
  const [filesError, updateFilesError] = useImmer("");

  // Auto-refresh file list when export finishes and panel is already open
  useEffect(() => {
    if (status === EXPORT_STATUS_DONE && filesOpen && onLoadFiles) {
      updateFilesLoading(true);
      updateFilesError("");
      onLoadFiles()
        .then((data) => updateFiles(data || []))
        .catch((e) => updateFilesError("Failed to load files: " + e.message))
        .finally(() => updateFilesLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const executeOnFirstClick = async () => {
    const result = await onFirstClick();
    if (!result) return;
    if (result.httpStatus === 409) {
      setFileExists(result.fileName || "");
    } else {
      startJob(result.jobId);
    }
  };

  const executeForceClick = async () => {
    reset();
    const result = await onForceClick();
    if (!result) return;
    startJob(result.jobId);
  };

  const toggleFiles = async () => {
    const opening = !filesOpen;
    updateFilesOpen(opening);
    if (opening && onLoadFiles) {
      updateFilesLoading(true);
      updateFilesError("");
      try {
        const data = await onLoadFiles();
        updateFiles(data || []);
      } catch (e) {
        updateFilesError("Failed to load files: " + e.message);
      } finally {
        updateFilesLoading(false);
      }
    }
  };

  const formatBytes = (bytes) => {
    if (bytes == null) return "";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className={containerClassName} style={{ textAlign: "right" }}>
      {/* Date-time format selector */}
      {onExportDateTimeFormatChange && (
        <div style={{ marginBottom: "4px", fontSize: "0.8em", display: "inline-flex", alignItems: "center", gap: "8px" }}>
          <span style={{ color: "#555" }}>Export time format:</span>
          <label style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}>
            <input
              type="radio"
              name="exportDateTimeFormat"
              value="UTC"
              checked={exportDateTimeFormat !== "LOCAL"}
              onChange={() => onExportDateTimeFormatChange("UTC")}
              style={{ margin: "0 -10px 0 0" }}
            />
            UTC
          </label>
          <label style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}>
            <input
              type="radio"
              name="exportDateTimeFormat"
              value="LOCAL"
              checked={exportDateTimeFormat === "LOCAL"}
              onChange={() => onExportDateTimeFormatChange("LOCAL")}
              style={{ margin: "0 -10px 0 0" }}
            />
            Local time
          </label>
        </div>
      )}
      {/* Top row: buttons side by side */}
      <div
        style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}
      >
        {onLoadFiles && (
          <button onClick={() => toggleFiles()}>
            {filesOpen ? "Hide exported files" : "Show exported files"}
          </button>
        )}

        {status === EXPORT_STATUS_INIT && (
          <button
            onClick={() => executeOnFirstClick()}
            disabled={disabled || isExportActive}
          >
            {caption}
          </button>
        )}

        {status === EXPORT_STATUS_FILE_EXISTS && (
          <>
            <button
              onClick={() => executeForceClick()}
              style={{ background: "#d4edda", borderColor: "#5cb85c" }}
            >
              Yes, generate again
            </button>
            <button
              onClick={() => reset()}
              style={{ background: "#f8d7da", borderColor: "#d9534f" }}
            >
              No, cancel
            </button>
          </>
        )}

        {status === EXPORT_STATUS_IN_PROGRESS && (
          <>
            <button disabled>Preparing export…</button>
            <span
              onClick={() => cancelJob()}
              title="Cancel export"
              style={{
                cursor: "pointer",
                color: "#c0392b",
                fontSize: "1.3em",
                lineHeight: 1,
                userSelect: "none",
                marginLeft: "4px",
              }}
            >
              &times;
            </span>
          </>
        )}

        {status === EXPORT_STATUS_DONE && (
          <a href={presignedUrl} onClick={() => reset()} download>
            <button style={{ background: "#d4edda", borderColor: "#5cb85c" }}>
              Download file
            </button>
          </a>
        )}

        {status === EXPORT_STATUS_FAILED && (
          <span style={{ color: "red" }}>
            {errorMessage}{" "}
            <a onClick={() => reset()} style={{ cursor: "pointer" }}>
              Retry
            </a>
          </span>
        )}
      </div>

      {/* File exists message */}
      {status === EXPORT_STATUS_FILE_EXISTS && (
        <div
          style={{
            marginTop: "4px",
            fontSize: "0.85em",
            color: "red",
            textAlign: "right",
          }}
        >
          File <strong>{existingFileName}</strong> already exists in S3.
        </div>
      )}

      {/* Progress bar */}
      {status === EXPORT_STATUS_IN_PROGRESS && (
        <div style={{ marginTop: "6px" }}>
          <div
            style={{
              height: "8px",
              background: "#e0e0e0",
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                borderRadius: "4px",
                background: "#4caf50",
                width: progress.percent !== null ? progress.percent + "%" : "100%",
                transition: progress.percent !== null ? "width 0.4s ease" : "none",
                animation:
                  progress.percent === null
                    ? "indeterminate 1.5s infinite linear"
                    : "none",
              }}
            />
          </div>
          {progress.totalRows !== null && (
            <div
              style={{
                fontSize: "0.8em",
                color: "#888",
                marginTop: "2px",
                textAlign: "right",
              }}
            >
              {progress.writtenRows.toLocaleString()} /{" "}
              {progress.totalRows.toLocaleString()} rows
              {getEta() && (
                <span style={{ marginLeft: "10px" }}>{getEta()}</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* File list panel */}
      {onLoadFiles && filesOpen && (
        <div
          style={{
            marginTop: "6px",
            border: "1px solid #ddd",
            borderRadius: "4px",
            maxHeight: "260px",
            overflowY: "auto",
            fontSize: "0.85em",
            textAlign: "left",
          }}
        >
          {filesLoading && (
            <div style={{ padding: "8px", color: "#888" }}>Loading…</div>
          )}
          {filesError && (
            <div style={{ padding: "8px", color: "red" }}>{filesError}</div>
          )}
          {!filesLoading && !filesError && files.length === 0 && (
            <div style={{ padding: "8px", color: "#888" }}>
              No export files found.
            </div>
          )}
          {!filesLoading &&
            !filesError &&
            files.map((f) => (
              <div
                key={f.fileName}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "6px 10px",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <div style={{ flex: 1, minWidth: 0, marginRight: "12px" }}>
                  <div
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontWeight: 500,
                    }}
                    title={f.fileName}
                  >
                    {f.fileName}
                  </div>
                  <div style={{ color: "#888", marginTop: "2px" }}>
                    {formatBytes(f.sizeBytes)} &middot; {formatDate(f.lastModified)}
                  </div>
                </div>
                <a
                  href={f.presignedUrl}
                  download
                  style={{ whiteSpace: "nowrap", color: "#1976d2" }}
                >
                  Download
                </a>
              </div>
            ))}
        </div>
      )}

      <style>{`
        @keyframes indeterminate {
          0%   { margin-left: -40%; width: 40%; }
          100% { margin-left: 100%; width: 40%; }
        }
      `}</style>
    </div>
  );
};

export default Component;
